import { useCallback, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import LoaderDots from "components/icons-svg/LoaderDots";
import { useMarkSearchUpdate } from "components/searchBar/mobile-tablet-view/drawer-buttons/useMarkSearchUpdate";
import useTrackSearchClick from "components/searchBar/mobile-tablet-view/drawer-buttons/useTrackSearchClick";
import useClearSearch from "components/searchBar/mobile-tablet-view/SearchLocation/UseClearSearch";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { selectLocalSearch, selectLocalSearchDateRange } from "stores/local-search/localSearchSlice";
import { setSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { setSearchQuery } from "stores/search/searchSlice";
import { markSearchFlow, selectUI } from "stores/ui/uiSlice";

// DrawerFooterButtons component controls the actions in the footer of a search drawer.
const DrawerFooterButtons = ({
  onClose,
  setIsSearchCleared,
  setIsDateCleared
}: {
  onClose: () => void;
  setIsSearchCleared: (value: boolean) => void;
  setIsDateCleared: (value: boolean) => void;
}) => {
  const secondaryButtonClassName = "h-11 w-1/2 rounded-lg border border-Gray-400 bg-white px-2 text-base font-semibold text-grayText shadow-[0px_4px_16px_0px_rgba(34,_34,_34,_0.08)] transition-colors duration-200 hover:border-primary-300 hover:text-brand-dark msm:min-w-[183px] sxm:w-auto";
  const primaryButtonClassName = "btn h-11 w-1/2 rounded-lg bg-primary-500 px-2 text-base font-bold text-white shadow-[0px_4px_16px_0px_rgba(34,_34,_34,_0.08)] transition-all duration-200 hover:bg-primary-300 focus:bg-primary-300 active:bg-primary-300 msm:min-w-[183px] sxm:w-auto";
  const router = useRouter();
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const localSearch = useAppSelector(selectLocalSearch);
  const localSearchDates = useAppSelector(selectLocalSearchDateRange);
  const dispatch = useAppDispatch();
  const track = useTrackSearchClick(); // Custom hook to call Mixpanel event

  const updateMarkSearch = useMarkSearchUpdate(); // Custom hook to update mark search data
  const clearSearch = useClearSearch(); // Custom hook to clear the search state

  const [loaderDots, setLoaderDots] = useState(false);
  const pathname = usePathname();
  const isListingPage = pathname?.startsWith("/vacation-rentals");
  const isHomePage = pathname === "/";
  const isLastMinutePage = pathname?.startsWith("/last-minute");
  const isLandingPage = isHomePage || pathname?.startsWith("/deals") || isLastMinutePage;

  /**
   * Function to handle the "Search" button click.
   * This function is used when the user is on the listing page.
   * It will update the URL with the new search parameters and will not redirect to the new URL, instead it will update the page without reloading/page-fetching. It will just fetch the new data from the API and update the state.
   */
  const onClickSearchWithoutPageReloadOrPush = useCallback(() => {
    if (localSearch.locationData.shortName === "" || localSearch.locationData.placeId === null) {
      // If location data is not present, redirect to location tab
      dispatch(markSearchFlow({
        currentSearchTab: "location"
      }));
    } else {
      // Combine local search data to form a single object
      const combinedSearchData = {
        ...localSearch.locationData,
        ...localSearch.dateRangeData,
        ...localSearch.guestDetails
      };
      setLoaderDots(true);
      // Update mark search data
      updateMarkSearch(combinedSearchData);
      dispatch(setSearchAddressResult({
        address_components: localSearch.searchAddressResult?.address_components || []
      }));
      if (isListingPage) {
        onClose(); // Close drawer if on listing page
      }
      dispatch(setSearchQuery({
        shouldUpdateUrl: true
      }));
      void track(combinedSearchData); // Trigger Mixpanel event
    }
  }, [localSearch, dispatch, updateMarkSearch, isListingPage, track, onClose]);

  /**
   * Function to handle the "Search" button click.
   * This function is used when the user is NOT on the listing page.
   * It will update the URL with the new search parameters and redirect to the new URL.
   */
  const onClickSearchWithPageUpdate = useCallback(() => {
    if (localSearch.locationData.shortName === "") {
      // If location data is not present, redirect to location tab
      dispatch(markSearchFlow({
        currentSearchTab: "location"
      }));
      return;
    }

    // Combine local search data to form a single object
    const combinedSearchData = {
      ...localSearch.locationData,
      ...localSearch.dateRangeData,
      ...localSearch.guestDetails
    };
    void track(combinedSearchData); // Trigger Mixpanel event

    if (!localSearch.newSearchURL) {
      return;
    }
    void router.push(localSearch.newSearchURL);
  }, [dispatch, localSearch]);

  // Function to handle the "Skip" button click
  const onClickSkip = () => {
    if (currentSearchTab === "dates") {
      dispatch(markSearchFlow({
        currentSearchTab: "guest"
      }));
    }
  };

  // Function to handle the "Next" button click
  const onClickNext = () => {
    dispatch(markSearchFlow({
      currentSearchTab: currentSearchTab === "location" ? "dates" : currentSearchTab === "dates" ? "guest" : "location"
    }));
  };
  return <div className='flex w-full items-center justify-between gap-4' data-sentry-component="DrawerFooterButtons" data-sentry-source-file="DrawerFooterButtons.tsx">
      {/*<!--------- left side button section -----------!> */}
      {currentSearchTab === "dates" ? <>
          {localSearchDates.startDate === null && localSearchDates.endDate === null ? <button type='button' aria-label='Skip Dates Accordion' className={secondaryButtonClassName} onClick={onClickSkip}>
              Skip
            </button> : <button type='button' aria-label='Reset Dates' className={secondaryButtonClassName} onClick={() => setIsDateCleared(true)}>
              Clear dates
            </button>}
        </> : <button type='button' aria-label='Clear button' className={secondaryButtonClassName} onClick={() => {
      clearSearch();
      setIsSearchCleared(true);
    }}>
          Clear all
        </button>}

      {/*<!--------- right side button section -----------!> */}
      {isLandingPage ? <>
          {currentSearchTab === "location" || currentSearchTab === "dates" ? <button type='button' aria-label='Next button' className={primaryButtonClassName} onClick={onClickNext}>
              Next
            </button> : <button aria-label='Search location button' type='button' className={primaryButtonClassName} onClick={onClickSearchWithPageUpdate}>
              {loaderDots ? <LoaderDots height={"60px"} width={"60px"} /> : "Search"}
            </button>}
        </> : <button aria-label='Search location button' type='button' className={primaryButtonClassName} onClick={onClickSearchWithoutPageReloadOrPush}>
          {loaderDots ? <LoaderDots height={"60px"} width={"60px"} /> : "Save"}
        </button>}
    </div>;
};
export default DrawerFooterButtons;